import { useState } from "react";
import { Link } from "react-router-dom";
import { useActiveAgencies } from "../hooks/useAgency";
import { usePublicAgencies } from "../hooks/usePublicAgencies";
import { validatePhone } from "../utils/validation";
import s from "./LandingPage.module.css";

export default function LandingPage({ onLogin, onRegister, onRegisterOrganization, onResetPassword, pendingApproval, setPendingApproval }) {
  /* ── Auth state ─────────────────────────────────── */
  const [authMode, setAuthMode] = useState("login"); // login | register | forgot
  const [registerType, setRegisterType] = useState("officer"); // officer | organization
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [badge, setBadge] = useState("");
  const [phone, setPhone] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [orgName, setOrgName] = useState("");
  const [selectedAgencyIds, setSelectedAgencyIds] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { agencies, loading: agenciesLoading } = useActiveAgencies();
  const { agencies: publicAgencies, loading: publicAgenciesLoading } = usePublicAgencies();

  const toggleAgency = (agencyId) => {
    setSelectedAgencyIds((prev) =>
      prev.includes(agencyId) ? prev.filter((id) => id !== agencyId) : [...prev, agencyId]
    );
  };

  /* ── Handlers ───────────────────────────────────── */
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setError("");
      setPendingApproval(false);
      setSubmitting(true);
      await onLogin(email.trim(), password);
    } catch (err) {
      if (err.code === "auth/pending-approval" || err.message === "PENDING_APPROVAL") {
        setPendingApproval(true);
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validatePassword = (pw) => {
    if (pw.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(pw)) return "Password must contain at least one uppercase letter.";
    if (!/[a-z]/.test(pw)) return "Password must contain at least one lowercase letter.";
    if (!/[0-9]/.test(pw)) return "Password must contain at least one number.";
    if (!/[^A-Za-z0-9]/.test(pw)) return "Password must contain at least one special character.";
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      setError("");

      if (registerType === "organization") {
        if (!name || !email || !password || !orgName) {
          setError("Name, organization name, email, and password are required.");
          return;
        }
        if (selectedAgencyIds.length === 0) {
          setError("Please select at least one agency to connect with.");
          return;
        }
        const pwErr = validatePassword(password);
        if (pwErr) { setError(pwErr); return; }
        setSubmitting(true);
        await onRegisterOrganization(name, orgName, email, phone, password, selectedAgencyIds);
      } else {
        if (!name || !email || !password || !badge || !phone) {
          setError("All fields are required.");
          return;
        }
        if (!agencyId) {
          setError("Please select your agency.");
          return;
        }
        const phoneErr = validatePhone(phone);
        if (phoneErr) { setError(phoneErr); return; }
        const pwErr = validatePassword(password);
        if (pwErr) { setError(pwErr); return; }
        setSubmitting(true);
        await onRegister(name, badge, email, phone, password, agencyId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    try {
      setError("");
      if (!email) {
        setError("Please enter your email address.");
        return;
      }
      setSubmitting(true);
      await onResetPassword(email);
      setSuccess("Password reset email sent. Check your inbox.");
      switchTab("login");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const switchTab = (mode) => {
    setAuthMode(mode);
    setError("");
    setSuccess("");
  };

  /* ── Render ─────────────────────────────────────── */
  return (
    <div className={s.page}>
      {/* ═══ NAV ═══ */}
      <header className={s.nav}>
        <div className={s.navInner}>
          <div className={s.navLeft}>
            <Link to="/" className={s.logoLink}>
              <span className={s.logoBadge}>OOD</span>
              <span className={s.logoText}>OfficerOnDuty</span>
            </Link>
          </div>
          <div className={s.navLinks}>
            <Link to="/features" className={s.navAnchor}>Features</Link>
            <Link to="/how-to" className={s.navAnchor}>How To</Link>
            <Link to="/request" className={s.navAnchor}>Request Officer</Link>
          </div>
        </div>
      </header>

      {/* ═══ MAIN — Auth centered ═══ */}
      <section className={s.heroCenter}>
        <h1 className={s.heroH1}>
          DUTY MANAGEMENT,{" "}
          <span className={s.heroAccent}>SIMPLIFIED.</span>
        </h1>
        <p className={s.heroDesc}>
          Off-duty scheduling built for law enforcement.
        </p>

        {/* ── Auth Card ── */}
        <div className={s.authCard}>
          <div className={s.authTabs}>
            <button
              className={`${s.authTab} ${authMode === "login" || authMode === "forgot" ? s.authTabActive : ""}`}
              onClick={() => switchTab("login")}
            >
              Sign In
            </button>
            <button
              className={`${s.authTab} ${authMode === "register" ? s.authTabActive : ""}`}
              onClick={() => switchTab("register")}
            >
              Register
            </button>
          </div>

          <div className={s.authBody}>
            {error && <div className={s.authError} role="alert">{error}</div>}
            {success && <div className={s.authSuccess} role="status">{success}</div>}

            {pendingApproval ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>{"\u23F3"}</div>
                <h3 style={{ margin: "0 0 8px", color: "var(--text)" }}>Registration Pending</h3>
                <p style={{ fontSize: 14, color: "var(--muted)", margin: "0 0 16px" }}>
                  An administrator will review your account shortly. You'll receive an email when approved.
                </p>
                <button
                  className={s.authBtn}
                  type="button"
                  onClick={() => { setPendingApproval(false); setError(""); setSuccess(""); }}
                >
                  Back to Sign In
                </button>
              </div>
            ) : authMode === "login" ? (
              <form onSubmit={handleLogin} aria-label="Sign in">
                <label className={s.fieldLabel} htmlFor="lp-email">Email</label>
                <input
                  id="lp-email"
                  className={s.authInput}
                  type="email"
                  placeholder="you@department.gov"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <label className={s.fieldLabel} htmlFor="lp-pass">Password</label>
                <input
                  id="lp-pass"
                  className={s.authInput}
                  type="password"
                  placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button className={s.authBtn} type="submit" disabled={submitting}>
                  {submitting ? "Signing in..." : "Sign In"}
                </button>
                <div className={s.authFooter}>
                  Forgot password?{" "}
                  <button type="button" onClick={() => switchTab("forgot")}>Reset</button>
                </div>
              </form>
            ) : authMode === "forgot" ? (
              <form onSubmit={handleForgot} aria-label="Reset password">
                <p style={{ fontSize: 14, color: "var(--muted)", margin: "0 0 16px" }}>
                  Enter your email and we'll send a reset link.
                </p>
                <label className={s.fieldLabel} htmlFor="lp-reset-email">Email</label>
                <input
                  id="lp-reset-email"
                  className={s.authInput}
                  type="email"
                  placeholder="you@department.gov"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button className={s.authBtn} type="submit" disabled={submitting}>
                  {submitting ? "Sending..." : "Send Reset Link"}
                </button>
                <div className={s.authFooter}>
                  <button type="button" onClick={() => switchTab("login")}>Back to Sign In</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleRegister} aria-label="Register">
                {/* ── Register type toggle ── */}
                <div className={s.registerToggle}>
                  <button
                    type="button"
                    className={`${s.registerToggleBtn} ${registerType === "officer" ? s.registerToggleBtnActive : ""}`}
                    onClick={() => { setRegisterType("officer"); setError(""); }}
                  >
                    Officer
                  </button>
                  <button
                    type="button"
                    className={`${s.registerToggleBtn} ${registerType === "organization" ? s.registerToggleBtnActive : ""}`}
                    onClick={() => { setRegisterType("organization"); setError(""); }}
                  >
                    Organization
                  </button>
                </div>

                {registerType === "officer" ? (
                  <>
                    {!agenciesLoading && agencies.length === 0 && (
                      <div className={s.authError} role="alert">
                        No agencies are currently accepting registrations.
                      </div>
                    )}
                    <label className={s.fieldLabel} htmlFor="lp-agency">Agency</label>
                    <select
                      id="lp-agency"
                      className={s.authSelect}
                      value={agencyId}
                      onChange={(e) => setAgencyId(e.target.value)}
                      required
                      disabled={agenciesLoading || agencies.length === 0}
                    >
                      <option value="">
                        {agenciesLoading ? "Loading..." : "Select Your Agency"}
                      </option>
                      {agencies.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                    <label className={s.fieldLabel} htmlFor="lp-name">Full Name</label>
                    <input
                      id="lp-name"
                      className={s.authInput}
                      type="text"
                      placeholder="Jane Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                    <label className={s.fieldLabel} htmlFor="lp-badge">Badge Number</label>
                    <input
                      id="lp-badge"
                      className={s.authInput}
                      type="text"
                      placeholder="OFC-201"
                      value={badge}
                      onChange={(e) => setBadge(e.target.value)}
                      required
                    />
                    <label className={s.fieldLabel} htmlFor="lp-phone">Phone</label>
                    <input
                      id="lp-phone"
                      className={s.authInput}
                      type="tel"
                      placeholder="555-123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                    <label className={s.fieldLabel} htmlFor="lp-reg-email">Email</label>
                    <input
                      id="lp-reg-email"
                      className={s.authInput}
                      type="email"
                      placeholder="you@department.gov"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <label className={s.fieldLabel} htmlFor="lp-reg-pass">Password</label>
                    <input
                      id="lp-reg-pass"
                      className={s.authInput}
                      type="password"
                      placeholder="Min 8 chars, upper, lower, number, special"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button className={s.authBtn} type="submit" disabled={submitting || agenciesLoading || agencies.length === 0}>
                      {submitting ? "Registering..." : "Register"}
                    </button>
                  </>
                ) : (
                  <>
                    <label className={s.fieldLabel} htmlFor="lp-org-name">Organization Name</label>
                    <input
                      id="lp-org-name"
                      className={s.authInput}
                      type="text"
                      placeholder="Acme Corp"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      required
                    />
                    <label className={s.fieldLabel} htmlFor="lp-org-contact">Contact Name</label>
                    <input
                      id="lp-org-contact"
                      className={s.authInput}
                      type="text"
                      placeholder="Jane Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                    <label className={s.fieldLabel} htmlFor="lp-org-email">Email</label>
                    <input
                      id="lp-org-email"
                      className={s.authInput}
                      type="email"
                      placeholder="you@organization.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <label className={s.fieldLabel} htmlFor="lp-org-phone">Phone</label>
                    <input
                      id="lp-org-phone"
                      className={s.authInput}
                      type="tel"
                      placeholder="555-123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <label className={s.fieldLabel} htmlFor="lp-org-pass">Password</label>
                    <input
                      id="lp-org-pass"
                      className={s.authInput}
                      type="password"
                      placeholder="Min 8 chars, upper, lower, number, special"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <div className={s.fieldLabel}>Connect with Agencies *</div>
                    <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--muted)" }}>
                      Select one or more agencies you'd like to submit officer requests to. Each agency will review your request separately.
                    </p>
                    {publicAgenciesLoading ? (
                      <div style={{ padding: "10px 14px", fontSize: 13, color: "var(--muted)" }}>
                        Loading agencies...
                      </div>
                    ) : publicAgencies.length === 0 ? (
                      <div className={s.authError} role="alert" style={{ marginBottom: 12 }}>
                        No agencies are currently accepting organization signups. Please check back later.
                      </div>
                    ) : (
                      <div style={{
                        maxHeight: 180, overflowY: "auto", border: "1px solid var(--border)",
                        borderRadius: 8, padding: "6px 4px", marginBottom: 12, background: "#fff",
                      }}>
                        {publicAgencies.map((a) => (
                          <label key={a.id} style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "6px 10px", cursor: "pointer", fontSize: 13,
                            borderRadius: 4,
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedAgencyIds.includes(a.id)}
                              onChange={() => toggleAgency(a.id)}
                            />
                            <span>{a.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <button className={s.authBtn} type="submit"
                      disabled={submitting || publicAgenciesLoading || publicAgencies.length === 0}>
                      {submitting ? "Registering..." : `Register${selectedAgencyIds.length > 0 ? ` (${selectedAgencyIds.length} ${selectedAgencyIds.length === 1 ? "agency" : "agencies"})` : ""}`}
                    </button>
                  </>
                )}
                <p className={s.authNote}>Requires admin approval after registration</p>
              </form>
            )}
          </div>
        </div>

      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className={s.footer}>
        <span className={s.footerCopy}>&copy; {new Date().getFullYear()} Primitive Edge LLC. All rights reserved.</span>
        <div className={s.footerLinks}>
          <Link to="/terms" className={s.footerLink}>Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
